import { RegistroCta } from '@/features/auth/components/registro-cta';
import {
  Rocket,
  Calendar,
  Monitor,
  ShieldCheck,
  Database,
  Headphones,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { DocsLayout } from '../components/docs-layout';

const onboardingSteps = [
  {
    step: 1,
    title: 'Registro y Configuracion Inicial',
    description: 'Crea tu cuenta de empresa y configura los parametros basicos.',
    details: [
      'Completa el registro con los datos de tu empresa (nombre, NIT, pais, segmento de industria).',
      'Se crea automaticamente tu espacio multi-tenant aislado.',
      'Tu primer usuario se configura como Administrador de Empresa.',
      'Recibes acceso inmediato al Dashboard y todas las funcionalidades.',
    ],
  },
  {
    step: 2,
    title: 'Configuracion de Datos Maestros',
    description: 'Carga la informacion base que necesita el sistema para operar.',
    details: [
      'Configura las divisas que manejas (USD, BOB, EUR) con sus tipos de cambio.',
      'Define tu estructura de lineas de negocio en 3 niveles (Sector > Vertical > Producto).',
      'Registra los paises donde operas.',
      'Carga tu catalogo de materiales con informacion arancelaria.',
    ],
  },
  {
    step: 3,
    title: 'Creacion de Modelos de Pricing',
    description: 'Define como se calculan los precios de tus productos y servicios.',
    details: [
      'Crea modelos con nombre, codigo, margen objetivo y margen piso.',
      'Agrega variables de distintos tipos (numericas, monetarias, formulas, tablas).',
      'Configura formulas calculadas que referencian otras variables.',
      'Define variables globales y por item segun tu logica de negocio.',
    ],
  },
  {
    step: 4,
    title: 'Operacion y Cotizacion',
    description: 'Comienza a crear cotizaciones profesionales con tu equipo.',
    details: [
      'Invita a vendedores y aprobadores a la plataforma.',
      'Crea tu primera cotizacion multi-seccion combinando tus modelos.',
      'Configura el flujo de aprobacion con los roles apropiados.',
      'Genera y exporta cotizaciones en PDF para tus clientes.',
    ],
  },
];

const timeline = [
  {
    week: 'Semana 1',
    title: 'Setup Inicial',
    tasks: [
      'Registro de la empresa y configuracion del espacio de trabajo',
      'Alta de usuarios (admin, vendedores, aprobadores)',
      'Configuracion de divisas y paises',
      'Capacitacion del equipo administrador',
    ],
    color: 'bg-blue-500',
  },
  {
    week: 'Semana 2',
    title: 'Datos Maestros',
    tasks: [
      'Carga de lineas de negocio y estructura organizacional',
      'Registro del catalogo de materiales',
      'Configuracion de tasas arancelarias e impuestos',
      'Validacion de datos con el equipo comercial',
    ],
    color: 'bg-violet-500',
  },
  {
    week: 'Semana 3',
    title: 'Modelos de Pricing',
    tasks: [
      'Creacion de modelos de pricing para cada linea de negocio',
      'Definicion de variables y formulas de calculo',
      'Pruebas con cotizaciones de ejemplo',
      'Ajustes y refinamiento de margenes',
    ],
    color: 'bg-emerald-500',
  },
  {
    week: 'Semana 4',
    title: 'Go-Live',
    tasks: [
      'Capacitacion final de vendedores y aprobadores',
      'Creacion de las primeras cotizaciones reales',
      'Validacion del flujo de aprobacion completo',
      'Monitoreo y soporte post-lanzamiento',
    ],
    color: 'bg-orange-500',
  },
];

const roles = [
  {
    name: 'PLATFORM_ADMIN',
    label: 'Administrador de Plataforma',
    description:
      'Acceso a nivel plataforma, gestion de todas las empresas y configuraciones globales.',
    permissions: [
      'Gestion de todos los tenants',
      'Configuracion global de la plataforma',
      'Acceso a metricas de uso',
    ],
  },
  {
    name: 'COMPANY_ADMIN',
    label: 'Administrador de Empresa',
    description:
      'Acceso total dentro de su empresa, gestiona modelos, datos, usuarios y configuraciones.',
    permissions: [
      'Gestionar modelos de pricing',
      'Configurar datos maestros',
      'Administrar usuarios y roles',
      'Ver todas las cotizaciones',
      'Aprobar cotizaciones',
      'Acceder a analytics',
    ],
  },
  {
    name: 'SALESPERSON',
    label: 'Vendedor',
    description:
      'Crea y gestiona sus propias cotizaciones. Acceso de lectura a modelos y materiales.',
    permissions: [
      'Crear y editar cotizaciones propias',
      'Ver modelos de pricing (solo lectura)',
      'Enviar cotizaciones para aprobacion',
      'Exportar cotizaciones a PDF',
      'Ver su propio dashboard',
    ],
  },
  {
    name: 'APPROVER',
    label: 'Aprobador',
    description: 'Revisa y aprueba cotizaciones del equipo de ventas. Ve todas las cotizaciones.',
    permissions: [
      'Ver todas las cotizaciones de la empresa',
      'Aprobar, rechazar o solicitar ajustes',
      'Ver panel de aprobaciones con estadisticas',
      'Recibir notificaciones de cotizaciones pendientes',
    ],
  },
];

export function ImplementationPage() {
  return (
    <DocsLayout
      title="Guia de Implementacion"
      description="Todo lo que necesitas saber para implementar Prixia en tu empresa, desde el registro hasta el go-live."
    >
      <div className="space-y-12">
        {/* Proceso de onboarding */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Proceso de Onboarding
            </h2>
          </div>

          <div className="relative pl-8 sm:pl-10 space-y-6">
            <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-gray-200 dark:bg-gray-800 sm:left-[19px]" />

            {onboardingSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="absolute -left-8 sm:-left-10 flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full border-2 border-blue-500 bg-white dark:bg-gray-950 text-sm font-bold text-blue-600 sm:text-base">
                  {step.step}
                </div>
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {step.description}
                    </p>
                    <ul className="mt-3 space-y-1.5">
                      {step.details.map((detail, dIdx) => (
                        <li
                          key={dIdx}
                          className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                        >
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-400 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Timeline Tipico de Implementacion
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {timeline.map((week) => (
              <Card key={week.week} className="overflow-hidden">
                <div className={`h-1.5 ${week.color}`} />
                <CardContent className="p-5">
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {week.week}
                  </span>
                  <h3 className="mt-1 text-base font-bold text-gray-900 dark:text-white">
                    {week.title}
                  </h3>
                  <ul className="mt-3 space-y-1.5">
                    {week.tasks.map((task, tIdx) => (
                      <li
                        key={tIdx}
                        className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400"
                      >
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                        <span>{task}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Requisitos tecnicos */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-400 flex items-center justify-center">
              <Monitor className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Requisitos Tecnicos</h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Navegadores Compatibles
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Google Chrome (version 90+)',
                      'Mozilla Firefox (version 88+)',
                      'Microsoft Edge (version 90+)',
                      'Safari (version 14+)',
                    ].map((browser, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                      >
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        {browser}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Requisitos de Red
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Conexion a internet estable (minimo 2 Mbps)',
                      'Acceso a puertos HTTPS (443)',
                      'No se requiere instalacion de software adicional',
                      'Compatible con redes corporativas con proxy',
                    ].map((req, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                      >
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Roles y permisos */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Roles y Permisos</h2>
          </div>

          <div className="space-y-4">
            {roles.map((role) => (
              <Card key={role.name}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 mt-0.5 text-xs font-mono font-bold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                      {role.name}
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {role.label}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {role.description}
                      </p>
                      <ul className="mt-3 space-y-1">
                        {role.permissions.map((perm, pIdx) => (
                          <li
                            key={pIdx}
                            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                          >
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500/60" />
                            {perm}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Migracion de datos */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Migracion de Datos</h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Prixia facilita la migracion de datos desde tus sistemas actuales a la plataforma:
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Materiales y Catalogo
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Registra tu catalogo de productos y servicios con codigos internos, codigos de
                    proveedor, partidas arancelarias y tasas vigentes. La importacion masiva via CSV
                    esta en desarrollo.
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Clientes
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Carga tu base de clientes con informacion de contacto. Los clientes se vinculan
                    automaticamente a las cotizaciones que generes.
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Datos Maestros
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Configura divisas, paises, lineas de negocio e impuestos desde la interfaz
                    administrativa. La carga inicial es simple y guiada.
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Integracion SAP
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Para empresas con SAP, la sincronizacion automatica de materiales y clientes
                    permite mantener los datos actualizados bidireccionalmlente.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Soporte */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-400 flex items-center justify-center">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Soporte Durante Implementacion
            </h2>
          </div>

          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Durante el proceso de implementacion, tu equipo cuenta con soporte dedicado para
                asegurar un lanzamiento exitoso:
              </p>
              <ul className="space-y-2">
                {[
                  'Sesion de kickoff con revision de objetivos y plan de implementacion.',
                  'Capacitacion personalizada para administradores, vendedores y aprobadores.',
                  'Asistencia en la configuracion de modelos de pricing y datos maestros.',
                  'Revision y validacion de las primeras cotizaciones de prueba.',
                  'Soporte prioritario durante las primeras 4 semanas post go-live.',
                  'Documentacion completa y acceso al centro de ayuda 24/7.',
                ].map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <div className="text-center py-12 px-6 rounded-2xl bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Comienza tu implementacion hoy
          </h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Registra tu empresa y en menos de 4 semanas tendras tu equipo operando con Prixia.
          </p>
          <RegistroCta
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3 rounded-lg hover:shadow-lg transition-shadow"
          >
            Crear Cuenta Gratis
            <ArrowRight className="w-4 h-4" />
          </RegistroCta>
        </div>
      </div>
    </DocsLayout>
  );
}
